/*
 * <dz-recs> — carrega recomendacoes dentro da gaveta do carrinho.
 *
 * Por que nao reusar o <product-recommendations> do tema:
 * ele so dispara o fetch por IntersectionObserver (global.js:1140). A gaveta
 * nasce fora da tela (transform translateX) e invisivel ate ganhar a classe
 * de aberta, entao o observer pode nunca intersectar e o bloco ficaria vazio.
 * Aqui o fetch acontece no connectedCallback, sem depender de visibilidade.
 *
 * O elemento se remove sozinho quando nao ha o que recomendar, para nao deixar
 * um titulo orfao ("Compre o look" sem nenhum produto embaixo).
 */
class DzRecs extends HTMLElement {
  connectedCallback() {
    const { url, productId, sectionId } = this.dataset;
    if (!url || !productId) {
      this.#desistir();
      return;
    }
    this.#carregar(`${url}&product_id=${productId}&section_id=${sectionId}`);
  }

  async #carregar(endereco) {
    try {
      const resposta = await fetch(endereco);
      if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);

      const doc = new DOMParser().parseFromString(await resposta.text(), 'text/html');
      const grade = doc.querySelector('.dz-recs__grade');
      const itens = grade ? grade.querySelectorAll('.dz-recs__item').length : 0;

      if (!itens) {
        this.#desistir();
        return;
      }

      this.innerHTML = grade.innerHTML;
      this.hidden = false;
      // o bloco inteiro (titulo + grade) so aparece quando ha o que mostrar
      this.closest('[data-dz-bloco]')?.removeAttribute('hidden');
    } catch (erro) {
      console.warn('[dz-recs] nao foi possivel carregar recomendacoes:', erro.message);
      this.#desistir();
    }
  }

  #desistir() {
    this.closest('[data-dz-bloco]')?.remove();
  }
}

if (!customElements.get('dz-recs')) {
  customElements.define('dz-recs', DzRecs);
}

/*
 * <dz-add> — adicionar direto da sugestao, escolhendo o tamanho no proprio card.
 *
 * Nao reusa o quick-add do tema porque todos os produtos da loja tem 3 ou 4
 * variantes: o quick_add 'standard' sempre abre um <quick-add-modal>, e esse
 * modal nao e movido para o body — dentro da gaveta ele ficaria preso no
 * empilhamento e no overflow. Aqui o tamanho e escolhido inline.
 *
 * Depois de adicionar, pede as mesmas secoes que o formulario de produto pede
 * e entrega para o renderContents da gaveta, para o carrinho, a barra de frete
 * e o contador se atualizarem de uma vez so.
 */
class DzAdd extends HTMLElement {
  connectedCallback() {
    if (this.dataset.dzPronto) return;
    this.dataset.dzPronto = '1';

    this.abrir = this.querySelector('[data-dz-add-abrir]');
    this.painel = this.querySelector('[data-dz-add-tamanhos]');
    this.aviso = this.querySelector('[data-dz-add-aviso]');
    if (!this.abrir || !this.painel) return;

    this.abrir.addEventListener('click', () => this.#alternar());
    this.querySelectorAll('[data-dz-variante]').forEach((b) =>
      b.addEventListener('click', () => this.#adicionar(b))
    );
  }

  #alternar() {
    const fechado = this.painel.hasAttribute('hidden');
    // so um card aberto por vez, senao a gaveta vira uma lista de menus
    this.closest('.dz-recs')
      ?.querySelectorAll('[data-dz-add-tamanhos]')
      .forEach((p) => p.setAttribute('hidden', ''));
    if (fechado) this.painel.removeAttribute('hidden');
    this.abrir.setAttribute('aria-expanded', String(fechado));
  }

  async #adicionar(botao) {
    const id = botao.dataset.dzVariante;
    const gaveta = document.querySelector('cart-drawer');
    botao.setAttribute('disabled', '');
    this.#dizer('Adicionando…');

    try {
      const secoes = gaveta
        ? gaveta.getSectionsToRender().map((s) => s.id)
        : ['cart-drawer', 'cart-icon-bubble'];

      const resp = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: secoes,
          sections_url: window.location.pathname,
        }),
      });
      const dados = await resp.json();

      if (dados.status) {
        // resposta de erro do Shopify traz `status` (ex.: 422 sem estoque)
        this.#dizer(dados.description || 'Não foi possível adicionar.', 'erro');
        botao.removeAttribute('disabled');
        return;
      }

      if (gaveta) {
        gaveta.renderContents(dados);
      } else {
        window.location.reload();
      }
    } catch (erro) {
      console.warn('[dz-add]', erro.message);
      this.#dizer('Não foi possível adicionar agora.', 'erro');
      botao.removeAttribute('disabled');
    }
  }

  #dizer(texto, tipo = '') {
    if (!this.aviso) return;
    this.aviso.textContent = texto;
    this.aviso.className = `dz-add__aviso${tipo ? ` dz-add__aviso--${tipo}` : ''}`;
  }
}

if (!customElements.get('dz-add')) {
  customElements.define('dz-add', DzAdd);
}
